import AddIcon from '@mui/icons-material/Add'
import { Box, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import DeleteIcon from '@/assets/images/Delete.svg'
import EditIcon from '@/assets/images/DescriptionEdit.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { useUpdateCaseDetailMutation } from '@/store/apiService'
import { getProjectPermissions } from '@/utils/constants'

interface RecipeDescriptionProps {
  caseDetails?: any
  caseId: string
  refetch?: () => void
}

function RecipeDescription({
  caseDetails,
  caseId,
  refetch,
}: RecipeDescriptionProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'description',
  })
  const [editOpen, setEditOpen] = useState(false)
  const [description, setDescription] = useState(caseDetails?.description || '')
  const [updateCaseDetail] = useUpdateCaseDetailMutation()
  const permissions = getProjectPermissions(caseDetails?.role)

  const handleEditDescription = async () => {
    try {
      await updateCaseDetail({
        caseId,
        description,
      }).unwrap()
      toast.success(t('createDescriptionDialog.saveSuccess'))
      refetch?.()
    } catch (error) {
      toast.error(t('createDescriptionDialog.saveFailed'))
    }
    setEditOpen(false)
  }

  const handleDeleteDescription = async () => {
    try {
      await updateCaseDetail({
        caseId,
        description: '',
      }).unwrap()
      toast.success(t('createDescriptionDialog.descriptionDeleted'))
      refetch?.()
    } catch (error) {
      toast.error(t('createDescriptionDialog.failedToDeleteDescription'))
    }
  }

  return (
    <>
      <Box
        bgcolor={'#121415'}
        minHeight={'653px'}
        width={'100%'}
        px={'80px'}
        py={'24px'}
        position={'relative'}
      >
        <Box
          bgcolor={'#2C2C2E'}
          borderRadius={2}
          p={'30px'}
          display="flex"
          flexDirection="column"
        >
          <Typography
            fontSize={'14px'}
            lineHeight={'20px'}
            fontWeight={400}
            color={caseDetails?.description ? '#FFFFFF' : '#C6C6C8'}
            whiteSpace="pre-line"
            p={caseDetails?.description ? 0 : 2}
            textAlign={caseDetails?.description ? 'left' : 'center'}
          >
            {caseDetails?.description || t('noDescription')}
          </Typography>

          {permissions?.canEditDescription && (
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
              {caseDetails?.description ? (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<img src={DeleteIcon} alt="Delete" />}
                    sx={{
                      color: '#E46962',
                      borderColor: '#E46962',
                      padding: '4px 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                    label={t('createDescriptionDialog.delete')}
                    textTransform="uppercase"
                    onClick={handleDeleteDescription}
                  />

                  <Button
                    variant="outlined"
                    startIcon={
                      <Box component="img" src={EditIcon} alt="Edit" />
                    }
                    sx={{
                      padding: '4px 10px',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                    label={t('createDescriptionDialog.edit')}
                    textTransform="uppercase"
                    onClick={() => {
                      setDescription(caseDetails?.description || '')
                      setEditOpen(true)
                    }}
                  />
                </>
              ) : (
                <Button
                  variant="outlined"
                  label={t('create')}
                  textTransform="uppercase"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setDescription('')
                    setEditOpen(true)
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title={
          <Typography fontSize={24} fontWeight={500}>
            {description
              ? t('createDescriptionDialog.editTitle')
              : t('createDescriptionDialog.createTitle')}
          </Typography>
        }
        content={
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('createDescriptionDialog.description')}
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              variant="outlined"
              size="medium"
              multiline
              rows={20}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& input': {
                    '&:-webkit-autofill': {
                      boxShadow: '0 0 0 1000px #292929 inset',
                      transition: 'background-color 5000s ease-in-out 0s',
                    },
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#5B5B5B',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#90CAF9',
                  },
                },
              }}
            />
          </Box>
        }
        actionButtons={
          <Box width="100%" gap={2} display="flex" justifyContent="flex-end">
            <Button
              label={t('createDescriptionDialog.cancelButton')}
              onClick={() => setEditOpen(false)}
              variant="outlined"
              textTransform="uppercase"
            />
            <Button
              label={t('createDescriptionDialog.saveButton')}
              onClick={handleEditDescription}
              variant="contained"
              disabled={!description.trim()}
              textTransform="uppercase"
            />
          </Box>
        }
        paperStyles={{ width: '800px', height: '700px' }}
        triggerElement={undefined}
      />
    </>
  )
}

export default RecipeDescription
